import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Spinner, Card, Row, Col, Badge, Button, Form } from 'react-bootstrap';
import moment from 'moment';
import { CheckCircle, XCircle, Plus, ArrowClockwise } from 'react-bootstrap-icons';
import { employeeAPI, shiftAPI } from '../../services';
import { useBranch } from '../../hooks/useBranch';
import { getUserFromLocalStorage } from '../../utils/auth';
import { toast } from 'react-toastify';

export default function ShiftDayDetailModal({
  show,
  onHide,
  selectedDate,
  shiftsForDate = [],
  onApproveShift,
  onRejectShift,
  onRevokeApproval,
  onUndoRejectShift,
  loading,
  onRefreshAfterBulk,
  onAddEmployee,
  mode = 'month',
}) {
  const { selectedBranch } = useBranch();
  const [employeeStats, setEmployeeStats] = useState({});
  const [statsLoading, setStatsLoading] = useState(false);
  const [shiftSlots, setShiftSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [expandedSlotHour, setExpandedSlotHour] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState({});
  const [employeesLoading, setEmployeesLoading] = useState({});
  const [creatingShifts, setCreatingShifts] = useState({});
  const dateLabel = selectedDate ? moment(selectedDate).format('dddd, DD/MM/YYYY') : '';

  const employeeIds = useMemo(() => {
    return Array.from(new Set(shiftsForDate.filter((s) => s.employee_id).map((s) => s.employee_id)));
  }, [shiftsForDate]);

  useEffect(() => {
    if (!show || !selectedDate || employeeIds.length === 0) return;
    let active = true;
    const loadStats = async () => {
      setStatsLoading(true);
      try {
        const stats = await Promise.all(
          employeeIds.map(async (id) => {
            if (mode === 'week') {
              const week = moment(selectedDate).format('YYYY-MM-DD');
              const [weeklyShifts, monthlyPoints, previousMonth, careerPoints] = await Promise.all([
                employeeAPI.getEmployeeWeeklyShiftsStats({ employeeId: id, week }),
                employeeAPI.getEmployeeMonthlyPointsStats({
                  employeeId: id,
                  month: moment(selectedDate).format('YYYY-MM'),
                }),
                employeeAPI.getEmployeePreviousMonthStats({
                  employeeId: id,
                  month: moment(selectedDate).format('YYYY-MM'),
                }),
                employeeAPI.getEmployeeCareerPoints({ employeeId: id }),
              ]);
              return [
                id,
                {
                  weeklyShifts: weeklyShifts?.data || weeklyShifts,
                  monthlyPoints: monthlyPoints?.data || monthlyPoints,
                  previousMonth: previousMonth?.data || previousMonth,
                  careerPoints: careerPoints?.data || careerPoints,
                },
              ];
            } else {
              const month = moment(selectedDate).format('YYYY-MM');
              const [monthlyShifts, monthlyPoints, previousMonth, careerPoints] = await Promise.all([
                employeeAPI.getEmployeeMonthlyShiftsStats({ employeeId: id, month }),
                employeeAPI.getEmployeeMonthlyPointsStats({ employeeId: id, month }),
                employeeAPI.getEmployeePreviousMonthStats({ employeeId: id, month }),
                employeeAPI.getEmployeeCareerPoints({ employeeId: id }),
              ]);
              return [
                id,
                {
                  monthlyShifts: monthlyShifts?.data || monthlyShifts,
                  monthlyPoints: monthlyPoints?.data || monthlyPoints,
                  previousMonth: previousMonth?.data || previousMonth,
                  careerPoints: careerPoints?.data || careerPoints,
                },
              ];
            }
          }),
        );
        if (!active) return;
        setEmployeeStats((prev) => {
          const next = { ...prev };
          stats.forEach(([id, data]) => {
            next[id] = data;
          });
          return next;
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading employee stats:', error);
      } finally {
        if (active) setStatsLoading(false);
      }
    };
    loadStats();
    return () => {
      active = false;
    };
  }, [show, selectedDate, employeeIds, mode]);

  // Load slots từ API
  useEffect(() => {
    if (!show || !selectedDate || !selectedBranch?.id) return;
    let active = true;
    const loadSlots = async () => {
      setSlotsLoading(true);
      try {
        const date = moment(selectedDate).format('YYYY-MM-DD');
        const response = await shiftAPI.getShiftSlots(selectedBranch.id, date);
        const slots = response?.data || response || [];
        if (!active) return;
        setShiftSlots(slots);
      } catch (error) {
        console.error('Error loading shift slots:', error);
        setShiftSlots([]);
      } finally {
        if (active) setSlotsLoading(false);
      }
    };
    loadSlots();
    return () => {
      active = false;
    };
  }, [show, selectedDate, selectedBranch?.id]);

  // Load danh sách nhân viên khi expand slot
  const loadEmployeesForSlot = async (slotKey, slotTemplate) => {
    if (!selectedBranch?.id || employeesLoading[slotKey]) return;

    if (!slotTemplate || !slotTemplate.template_id || !slotTemplate.start_time || !slotTemplate.end_time) {
      console.error('Missing template info:', slotTemplate);
      return;
    }

    setEmployeesLoading((prev) => ({ ...prev, [slotKey]: true }));
    try {
      const response = await shiftAPI.getAvailableEmployees(
        slotTemplate.template_id,
        slotTemplate.start_time,
        slotTemplate.end_time,
      );

      const availableEmployees = response?.available_employees || response?.data?.available_employees || [];
      const employeesArray = Array.isArray(availableEmployees) ? availableEmployees : [];

      setEmployees((prev) => ({
        ...prev,
        [slotKey]: employeesArray,
      }));

      // Không cần load stats nữa vì API đã trả về đầy đủ thông tin
    } catch (error) {
      console.error('Error loading available employees:', error);
      toast.error('Lỗi khi tải danh sách nhân viên');
      setEmployees((prev) => ({ ...prev, [slotKey]: [] }));
    } finally {
      setEmployeesLoading((prev) => ({ ...prev, [slotKey]: false }));
    }
  };

  // Helper function để tính slot date từ start_time
  const getSlotDate = useCallback((startTime) => {
    return moment(startTime).format('YYYY-MM-DD');
  }, []);

  // Helper function để tạo slot key duy nhất
  const getSlotKey = useCallback(
    (slot) => {
      const date = getSlotDate(slot.start_time);
      return `${slot.template_id}_${date}`;
    },
    [getSlotDate],
  );

  // Nhóm slots theo slot key (template_id + date) thay vì theo giờ
  const timeSlots = useMemo(() => {
    // Tạo map để nhóm slots theo slot key
    const slotMap = new Map();

    // Nhóm shiftSlots theo slot key
    shiftSlots.forEach((slot) => {
      const slotKey = getSlotKey(slot);
      const startTime = moment(slot.start_time);
      const hour = startTime.hour();
      const minute = startTime.minute();

      if (!slotMap.has(slotKey)) {
        slotMap.set(slotKey, {
          slotKey,
          hour,
          hourLabel: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
          slotTemplates: [],
          shifts: [],
        });
      }

      slotMap.get(slotKey).slotTemplates.push(slot);
    });

    // Lọc bỏ ca thất bại (FAILED status)
    const validShifts = shiftsForDate.filter((s) => s.status !== 'FAILED');

    // Map shifts vào slot tương ứng dựa trên template_id và date
    validShifts.forEach((shift) => {
      // Tìm slot template khớp với template_id và date của shift
      let matchedSlot = null;

      // Tìm theo template_id và date từ shift start_time hoặc date
      const shiftDate = shift.start_time
        ? getSlotDate(shift.start_time)
        : shift.date || moment(selectedDate).format('YYYY-MM-DD');

      const shiftSlotKey = `${shift.template_id}_${shiftDate}`;

      if (slotMap.has(shiftSlotKey)) {
        matchedSlot = slotMap.get(shiftSlotKey);
      } else {
        // Fallback: tìm theo template_id trong bất kỳ slot nào
        for (const [, slot] of slotMap.entries()) {
          const slotTemplate = slot.slotTemplates.find((st) => st.template_id === shift.template_id);
          if (slotTemplate) {
            matchedSlot = slot;
            break;
          }
        }
      }

      // Nếu vẫn không tìm thấy, parse giờ từ time string và tạo slot mới
      if (!matchedSlot) {
        const timeStr = shift.time || '';
        const match = timeStr.match(/(\d{1,2}):(\d{2})/);
        if (match) {
          const hour = parseInt(match[1], 10);
          const minute = parseInt(match[2], 10);
          if (hour >= 0 && hour < 24) {
            const fallbackKey = `fallback_${shift.template_id || 'unknown'}_${shiftDate}`;
            if (!slotMap.has(fallbackKey)) {
              slotMap.set(fallbackKey, {
                slotKey: fallbackKey,
                hour,
                hourLabel: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
                slotTemplates: [],
                shifts: [],
              });
            }
            matchedSlot = slotMap.get(fallbackKey);
          }
        }
      }

      if (matchedSlot) {
        matchedSlot.shifts.push(shift);
      }
    });

    // Chuyển map thành array và sắp xếp theo giờ, sau đó theo phút
    const slots = Array.from(slotMap.values()).sort((a, b) => {
      if (a.hour !== b.hour) {
        return a.hour - b.hour;
      }
      // Nếu cùng giờ, sắp xếp theo phút từ slotTemplates
      const aMinute = a.slotTemplates[0] ? moment(a.slotTemplates[0].start_time).minute() : 0;
      const bMinute = b.slotTemplates[0] ? moment(b.slotTemplates[0].start_time).minute() : 0;
      return aMinute - bMinute;
    });

    // Sắp xếp ca trong mỗi slot: ca ưu tiên lên đầu, sau đó theo tên nhân viên
    slots.forEach((slot) => {
      slot.shifts.sort((a, b) => {
        if (a.is_preferred !== b.is_preferred) {
          return b.is_preferred ? 1 : -1;
        }
        return (a.employee_name || '').localeCompare(b.employee_name || '');
      });
    });

    return slots;
  }, [shiftSlots, shiftsForDate, selectedDate, getSlotKey, getSlotDate]);

  // #190609 (lệnh sếp 19:42 04/09/2026) — "không được hiển thị lẫn lộn lên trên khu vực duyệt ca
  // nữa, hãy hiển thị một section show đề xuất của bot riêng". Khu duyệt ca chỉ còn đăng ký thật
  // của nhân viên; ca PROPOSED_BOT (bot ĐỀ XUẤT, chưa duyệt) dồn hết xuống section riêng bên dưới.
  // `botProposalsInSlot` giữ lại các đề xuất đã bị tách khỏi slot: thẻ thì xuống section riêng,
  // nhưng con số "x/y người" của slot vẫn phải đếm chúng — nếu không quản lý nhìn slot đã có
  // người đăng ký lại tưởng đang trống trơn.
  const approvalSlots = useMemo(
    () =>
      timeSlots.map((slot) => ({
        ...slot,
        shifts: slot.shifts.filter((s) => s.status !== 'PROPOSED_BOT'),
        botProposalsInSlot: slot.shifts.filter((s) => s.status === 'PROPOSED_BOT').length,
      })),
    [timeSlots],
  );
  const botProposalSlots = useMemo(
    () =>
      timeSlots
        .map((slot) => ({ ...slot, shifts: slot.shifts.filter((s) => s.status === 'PROPOSED_BOT') }))
        .filter((slot) => slot.shifts.length > 0),
    [timeSlots],
  );
  const botProposalCount = useMemo(
    () => botProposalSlots.reduce((sum, slot) => sum + slot.shifts.length, 0),
    [botProposalSlots],
  );

  const handleToggleAddEmployee = (slotKey, slotInfo) => {
    if (expandedSlotHour === slotKey) {
      setExpandedSlotHour(null);
    } else {
      setExpandedSlotHour(slotKey);
      // Luôn reload để lấy dữ liệu mới nhất
      if (slotInfo) {
        loadEmployeesForSlot(slotKey, slotInfo);
      } else {
        // Fallback: lấy từ timeSlots nếu không có slotInfo
        const slot = timeSlots.find((s) => s.slotKey === slotKey);
        if (slot && slot.slotTemplates && slot.slotTemplates.length > 0) {
          loadEmployeesForSlot(slotKey, slot.slotTemplates[0]);
        }
      }
    }
  };

  const handleCreateShifts = async (slotKey, slotInfo) => {
    const employeeIds = selectedEmployeeIds[slotKey] || [];
    if (employeeIds.length === 0 || !selectedBranch?.id) {
      toast.error('Vui lòng chọn ít nhất một nhân viên');
      return;
    }

    const user = getUserFromLocalStorage();
    if (!user?.id) {
      toast.error('Không tìm thấy thông tin người dùng');
      return;
    }

    setCreatingShifts((prev) => ({ ...prev, [slotKey]: true }));
    try {
      const items = employeeIds.map((employeeId) => ({
        employee_id: employeeId,
        branch_id: selectedBranch.id,
        start_time: slotInfo.start_time,
        end_time: slotInfo.end_time,
        template_id: slotInfo.template_id,
      }));

      const res = await shiftAPI.createBulkShifts({
        current_user_id: user.id,
        items,
      });

      // API /shifts/bulk trả kết quả THEO TỪNG DÒNG (`results[i].success`) và luôn HTTP 200,
      // kể cả khi có dòng bị từ chối. Trước #005135 màn này luôn báo "thành công!" nên mọi lý
      // do từ chối của server đều bị nuốt — nay hiện đúng lỗi (vd NV còn trong tháng đầu tập
      // việc nên chưa nhận được ca chính).
      const results = Array.isArray(res?.results) ? res.results : [];
      const failed = results.filter((r) => !r?.success);
      const okCount = results.length > 0 ? results.length - failed.length : employeeIds.length;

      if (okCount > 0) {
        toast.success(`Đã tạo ${okCount} ca thành công!`);
      }
      failed.forEach((r) => {
        const name =
          (employees[slotKey] || []).find(
            (e) => e.id === items[r.index]?.employee_id,
          )?.full_name ??
          items[r.index]?.employee_id ??
          'Nhân viên';
        toast.error(`${name}: ${r.error || 'Không tạo được ca'}`, { autoClose: 8000 });
      });

      setExpandedSlotHour(null);
      setSelectedEmployeeIds((prev) => ({ ...prev, [slotKey]: [] }));
      onRefreshAfterBulk?.();
    } catch (error) {
      console.error('Error creating shifts:', error);
      toast.error('Lỗi khi tạo ca cho nhân viên');
    } finally {
      setCreatingShifts((prev) => ({ ...prev, [slotKey]: false }));
    }
  };

  // #190609 — thẻ ca dùng chung cho CẢ khu duyệt ca lẫn section "Đề xuất của bot": sếp yêu
  // cầu 2 khu "có giao diện cũng giống như duyệt vậy", nên tách khỏi vòng lặp slot để không
  // phải chép 2 bản thẻ rồi lệch nhau về sau.
  const renderShiftCard = (shift) => {
                        const STATUS_CONFIG = {
                          PENDING:                { label: 'Chờ duyệt',    color: 'warning'   },
                          APPROVED:               { label: 'Đã duyệt',     color: 'success'   },
                          // #190609 — bot KHÔNG còn tự duyệt. PROPOSED_BOT = ĐỀ XUẤT (chưa duyệt),
                          // nằm ở section riêng. APPROVE_BOT chỉ còn là DỮ LIỆU CŨ của cơ chế đã bỏ.
                          PROPOSED_BOT:           { label: '🤖 Bot đề xuất', color: 'warning' },
                          APPROVE_BOT:            { label: 'Đã duyệt (bot cũ)', color: 'success' },
                          IN_PROGRESS:            { label: 'Đang làm',     color: 'primary'   },
                          COMPLETED:              { label: 'Hoàn thành',   color: 'secondary' },
                          COMPLETED_WAIT_APPROVE: { label: 'Chờ xác nhận', color: 'warning'   },
                          REJECTED:               { label: 'Từ chối',      color: 'danger'    },
                          CANCELLED:              { label: 'Đã hủy',       color: 'secondary' },
                          ABSENT:                 { label: 'Vắng mặt',     color: 'danger'    },
                        };
                        const { label: statusLabel, color: variant } =
                          STATUS_CONFIG[shift.status] || { label: shift.status, color: 'secondary' };
                        const stats = employeeStats[shift.employee_id];
                        const weeklyShifts = stats?.weeklyShifts || {};
                        const monthlyShifts = stats?.monthlyShifts || {};
                        const shiftsData = mode === 'week' ? weeklyShifts : monthlyShifts;
                        const monthlyPoints = stats?.monthlyPoints || {};
                        const previousMonth = stats?.previousMonth || {};
                        const careerPoints = stats?.careerPoints || {};

                        return (
                          <Card
                            key={shift.id}
                            className={`border-${variant} ${shift.is_preferred ? 'border-info border-2' : ''}`}
                          >
                            <Card.Body>
                              <Row className="align-items-center">
                                <Col>
                                  <div className="d-flex align-items-center gap-2 mb-1">
                                    <div className="fw-bold">{shift.employee_name}</div>
                                    {shift.is_preferred && <Badge bg="info">⭐ Ca ưu tiên</Badge>}
                                  </div>
                                  <div className="text-muted">{shift.time}</div>
                                  {(shift.approved_shifts || shift.pending_shifts || shift.rejected_shifts) && (
                                    <div className="small text-muted mt-1">
                                      <span>
                                        ✅ {shift.approved_shifts || 0} Đã duyệt | ⏳ {shift.pending_shifts || 0} Chờ
                                        duyệt | ❌ {shift.rejected_shifts || 0} Từ chối
                                      </span>
                                    </div>
                                  )}
                                </Col>
                                <Col xs="auto">
                                  <div className="d-flex align-items-center gap-2">
                                    <Badge bg={variant}>{statusLabel}</Badge>
                                    {(shift.status === 'PENDING' ||
                                      // #190609 — nút Duyệt/Từ chối của section "Đề xuất của bot"
                                      // dùng chung đúng 2 nút này ⇒ bấm Duyệt đi thẳng đường duyệt
                                      // tay, ghi đủ bộ trường y hệt.
                                      shift.status === 'PROPOSED_BOT') && (
                                      <>
                                        <Button
                                          size="sm"
                                          variant="success"
                                          onClick={() => onApproveShift(shift.id)}
                                          disabled={loading}
                                          className="d-flex align-items-center gap-1"
                                        >
                                          <CheckCircle size={16} />
                                          Duyệt
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="danger"
                                          onClick={() => onRejectShift(shift.id)}
                                          disabled={loading}
                                          className="d-flex align-items-center gap-1"
                                        >
                                          <XCircle size={16} />
                                          Từ chối
                                        </Button>
                                      </>
                                    )}
                                    {shift.status === 'APPROVE_BOT' && (
                                      <>
                                        <Button
                                          size="sm"
                                          variant="success"
                                          onClick={() => onApproveShift(shift.id)}
                                          disabled={loading}
                                          className="d-flex align-items-center gap-1"
                                          title="Duyệt chính thức thay cho bot"
                                        >
                                          <CheckCircle size={16} />
                                          Duyệt
                                        </Button>
                                        {onRevokeApproval && (
                                          <Button
                                            size="sm"
                                            variant="outline-warning"
                                            onClick={() => onRevokeApproval(shift.id)}
                                            disabled={loading}
                                            className="d-flex align-items-center gap-1"
                                            title="Hủy duyệt bot, trả về PENDING"
                                          >
                                            <ArrowClockwise size={16} />
                                            Revoke
                                          </Button>
                                        )}
                                        <Button
                                          size="sm"
                                          variant="outline-danger"
                                          onClick={() => onRejectShift(shift.id)}
                                          disabled={loading}
                                          className="d-flex align-items-center gap-1"
                                          title="Từ chối ca"
                                        >
                                          <XCircle size={16} />
                                          Từ chối
                                        </Button>
                                      </>
                                    )}
                                    {shift.status === 'APPROVED' && onRevokeApproval && (
                                      <Button
                                        size="sm"
                                        variant="outline-warning"
                                        onClick={() => onRevokeApproval(shift.id)}
                                        disabled={loading}
                                        className="d-flex align-items-center gap-1"
                                      >
                                        <ArrowClockwise size={16} />
                                        Hủy duyệt
                                      </Button>
                                    )}
                                    {/* #191746 — bấm nhầm "Từ chối" thì hủy được, ca về lại chờ duyệt */}
                                    {shift.status === 'REJECTED' && onUndoRejectShift && (
                                      <Button
                                        size="sm"
                                        variant="outline-warning"
                                        onClick={() => onUndoRejectShift(shift.id)}
                                        disabled={loading}
                                        className="d-flex align-items-center gap-1"
                                        title="Hủy lệnh từ chối, trả ca về chờ duyệt"
                                      >
                                        <ArrowClockwise size={16} />
                                        Hủy từ chối
                                      </Button>
                                    )}
                                  </div>
                                </Col>
                              </Row>

                              {stats && (
                                <div className="mt-3 small text-muted">
                                  <div>
                                    <strong>Điểm tháng:</strong> {monthlyPoints.net_points ?? 0} (Thưởng{' '}
                                    {monthlyPoints.reward_points ?? 0} - Phạt {monthlyPoints.penalty_points ?? 0} -
                                    Chuyên cần {monthlyPoints.attendance_points ?? 0})
                                  </div>
                                  <div>
                                    <strong>{mode === 'week' ? 'Ca tuần này' : 'Ca tháng này'}:</strong> Đã duyệt{' '}
                                    {shiftsData.approved_shifts ?? 0} - Chờ duyệt {shiftsData.pending_shifts ?? 0} - Từ
                                    chối {shiftsData.rejected_shifts ?? 0}
                                  </div>
                                  <div>
                                    <strong>Ca hoàn thành tháng trước:</strong> {previousMonth.completed_shifts ?? 0}
                                  </div>
                                  <div>
                                    <strong>Tổng điểm tích lũy:</strong> {careerPoints.total_accumulated_points ?? 0}{' '}
                                    (Thưởng {careerPoints.total_reward_points ?? 0} - Phạt{' '}
                                    {careerPoints.total_penalty_points ?? 0})
                                  </div>
                                </div>
                              )}
                            </Card.Body>
                          </Card>
                        );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>Chi tiết ca làm</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <strong>{dateLabel}</strong>
        </div>

        {slotsLoading && (
          <div className="text-center text-muted small d-flex align-items-center justify-content-center gap-2 py-2">
            <Spinner
              animation="border"
              size="sm"
            />
            Đang tải slots...
          </div>
        )}

        {statsLoading && (
          <div className="text-center text-muted small d-flex align-items-center justify-content-center gap-2 py-2">
            <Spinner
              animation="border"
              size="sm"
            />
            Đang tải báo cáo nhân viên...
          </div>
        )}

        <div className="d-flex flex-column gap-2">
          {approvalSlots.map((slot) => {
            const hasShifts = slot.shifts.length > 0;
            const hasSlotTemplates = slot.slotTemplates.length > 0;
            const hasPreferred = slot.shifts.some((s) => s.is_preferred);

            // Nếu không có slot template và không có ca thì ẩn slot trống
            if (!hasSlotTemplates && !hasShifts) {
              return null;
            }

            // Tính toán số lượng nhân viên cần thiết và hiện tại
            const maxEmployees = slot.slotTemplates.reduce((max, st) => Math.max(max, st.max_employees || 0), 0);
            const currentEmployees =
              slot.shifts.filter((s) => s.status !== 'REJECTED' && s.status !== 'CANCELLED').length +
              (slot.botProposalsInSlot || 0);
            const registeredEmployees = slot.shifts.filter(
              (s) => s.status === 'APPROVED' || s.status === 'APPROVE_BOT',
            ).length;
            const needsMoreEmployees = currentEmployees < maxEmployees;

            // Kiểm tra xem ngày có phải quá khứ không
            const isPastDate = selectedDate ? moment(selectedDate).isBefore(moment().startOf('day')) : false;

            return (
              <Card
                key={slot.slotKey}
                className={`mb-2 ${hasPreferred ? 'border-info border-2' : ''} ${
                  !hasShifts && hasSlotTemplates ? 'border-secondary' : ''
                }`}
              >
                <Card.Header className="d-flex justify-content-between align-items-center py-2">
                  <div className="d-flex align-items-center gap-3">
                    <div className="fw-bold text-primary">{slot.hourLabel}</div>
                    {hasSlotTemplates && (
                      <div className="d-flex gap-2 flex-wrap">
                        {slot.slotTemplates.map((st, idx) => (
                          <Badge
                            key={idx}
                            bg="secondary"
                            className="text-white"
                          >
                            {st.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {maxEmployees > 0 && (
                      <>
                        {' '}
                        <Badge bg={needsMoreEmployees ? 'warning' : 'success'}>
                          {currentEmployees}/{maxEmployees} người
                        </Badge>
                        <Badge bg={registeredEmployees < maxEmployees ? 'warning' : 'success'}>
                          {registeredEmployees}/{maxEmployees} đã duyệt
                        </Badge>
                      </>
                    )}
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {hasShifts && <Badge bg="info">{slot.shifts.length} ca</Badge>}
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => {
                        const slotTemplate = slot.slotTemplates[0];
                        if (slotTemplate) {
                          if (onAddEmployee) {
                            onAddEmployee({
                              template_id: slotTemplate.template_id,
                              start_time: slotTemplate.start_time,
                              end_time: slotTemplate.end_time,
                              name: slotTemplate.name,
                              slotKey: slot.slotKey,
                            });
                          } else {
                            handleToggleAddEmployee(slot.slotKey, {
                              template_id: slotTemplate.template_id,
                              start_time: slotTemplate.start_time,
                              end_time: slotTemplate.end_time,
                              name: slotTemplate.name,
                              slotKey: slot.slotKey,
                            });
                          }
                        }
                      }}
                      className="d-flex align-items-center gap-1"
                    >
                      <Plus size={14} />
                      Thêm nhân viên
                    </Button>
                  </div>
                </Card.Header>
                {hasShifts ? (
                  <Card.Body className="py-2">
                    <div className="d-flex flex-column gap-2">
                      {slot.shifts.map((shift) => renderShiftCard(shift))}
                    </div>
                  </Card.Body>
                ) : (
                  <Card.Body className="py-2 text-muted text-center small">
                    Chưa có ca đăng ký
                    {slot.botProposalsInSlot > 0 && (
                      <> — {slot.botProposalsInSlot} đề xuất của bot đang chờ ở mục "🤖 Đề xuất của bot" bên dưới</>
                    )}
                  </Card.Body>
                )}

                {/* Form thêm nhân viên inline */}
                {expandedSlotHour === slot.slotKey && (
                  <Card.Body className="py-2 border-top bg-light">
                    <div className="mb-2">
                      <strong>Chọn nhân viên để thêm vào ca:</strong>
                    </div>
                    {employeesLoading[slot.slotKey] ? (
                      <div className="text-center py-3">
                        <Spinner
                          animation="border"
                          size="sm"
                        />
                        <div className="mt-2 text-muted small">Đang tải danh sách nhân viên...</div>
                      </div>
                    ) : (
                      <Form>
                        <div
                          className="border rounded p-2 bg-white"
                          style={{ maxHeight: '200px', overflowY: 'auto' }}
                        >
                          {!employees[slot.slotKey] || employees[slot.slotKey].length === 0 ? (
                            <div className="text-center text-muted py-2 small">Không có nhân viên nào</div>
                          ) : (
                            employees[slot.slotKey].map((employee) => {
                              const isAvailable = employee.is_available !== false;
                              const hasConflict = employee.has_conflict === true;
                              const isPreferred = employee.is_preferred === true;
                              const hasPreference = employee.has_preference === true;

                              return (
                                <Form.Check
                                  key={employee.id}
                                  type="checkbox"
                                  id={`employee-${slot.slotKey}-${employee.id}`}
                                  checked={(selectedEmployeeIds[slot.slotKey] || []).includes(employee.id)}
                                  onChange={(e) => {
                                    const currentIds = selectedEmployeeIds[slot.slotKey] || [];
                                    if (e.target.checked) {
                                      setSelectedEmployeeIds({
                                        ...selectedEmployeeIds,
                                        [slot.slotKey]: [...currentIds, employee.id],
                                      });
                                    } else {
                                      setSelectedEmployeeIds({
                                        ...selectedEmployeeIds,
                                        [slot.slotKey]: currentIds.filter((id) => id !== employee.id),
                                      });
                                    }
                                  }}
                                  disabled={!isAvailable}
                                  className="mb-2"
                                  label={
                                    <div>
                                      <div className="d-flex align-items-center gap-2">
                                        <span className="fw-bold">{employee.full_name}</span>
                                        {isPreferred && (
                                          <Badge
                                            bg="info"
                                            className="small"
                                          >
                                            ⭐ Ca ưu tiên
                                          </Badge>
                                        )}
                                        {hasPreference && !isPreferred && (
                                          <Badge
                                            bg="secondary"
                                            className="small"
                                          >
                                            Có ưu tiên
                                          </Badge>
                                        )}
                                        {hasConflict && (
                                          <Badge
                                            bg="danger"
                                            className="small"
                                          >
                                            Xung đột
                                          </Badge>
                                        )}
                                        {!isAvailable && (
                                          <Badge
                                            bg="warning"
                                            className="small"
                                          >
                                            Không khả dụng
                                          </Badge>
                                        )}
                                      </div>
                                      <div className="small text-muted mt-1">
                                        {employee.username && <span className="me-2">@{employee.username}</span>}
                                        <span>
                                          ✅ {employee.approved_shifts || 0} Đã duyệt | ⏳{' '}
                                          {employee.pending_shifts || 0} Chờ duyệt | ❌ {employee.rejected_shifts || 0}{' '}
                                          Từ chối
                                        </span>
                                      </div>
                                    </div>
                                  }
                                />
                              );
                            })
                          )}
                        </div>
                        <div className="d-flex justify-content-end gap-2 mt-3">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                              setExpandedSlotHour(null);
                              setSelectedEmployeeIds((prev) => ({ ...prev, [slot.slotKey]: [] }));
                            }}
                          >
                            Hủy
                          </Button>
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => {
                              const slotTemplate = slot.slotTemplates[0];
                              if (slotTemplate) {
                                handleCreateShifts(slot.slotKey, {
                                  template_id: slotTemplate.template_id,
                                  start_time: slotTemplate.start_time,
                                  end_time: slotTemplate.end_time,
                                  name: slotTemplate.name,
                                });
                              }
                            }}
                            disabled={
                              !selectedEmployeeIds[slot.slotKey] ||
                              selectedEmployeeIds[slot.slotKey].length === 0 ||
                              creatingShifts[slot.slotKey]
                            }
                          >
                            {creatingShifts[slot.slotKey] ? (
                              <>
                                <Spinner
                                  animation="border"
                                  size="sm"
                                  className="me-2"
                                />
                                Đang tạo...
                              </>
                            ) : (
                              `Tạo ca cho ${(selectedEmployeeIds[slot.slotKey] || []).length} nhân viên`
                            )}
                          </Button>
                        </div>
                      </Form>
                    )}
                  </Card.Body>
                )}
              </Card>
            );
          })}
        </div>

        {!slotsLoading && botProposalCount === 0 && approvalSlots.every((s) => !s.slotTemplates.length && !s.shifts.length) && (
          <div className="text-center py-4 text-muted">Không có slot nào trong ngày này</div>
        )}

        {/* #190609 — SECTION RIÊNG: đề xuất của bot. Cùng bộ thẻ với khu duyệt ca (renderShiftCard)
            nhưng KHÔNG nằm chung khu, không tính vào bộ đếm "đã duyệt", chưa chiếm slot. Bấm Duyệt
            ở đây đi đúng đường duyệt tay ⇒ ca rời section này và sang khu duyệt ca ở trên. */}
        {botProposalCount > 0 && (
          <div className="mt-4">
            <div className="d-flex align-items-center gap-2 mb-2 pt-3 border-top">
              <h6 className="mb-0">🤖 Đề xuất của bot</h6>
              <Badge bg="warning">{botProposalCount} đề xuất</Badge>
            </div>
            <div className="text-muted small mb-2">
              Bot chỉ ĐỀ XUẤT, chưa duyệt. Ca ở đây chưa được tính vào "Đã duyệt" và nhân viên chưa nhận
              được ca — bấm <strong>Duyệt</strong> thì ca mới chuyển thành ca đã duyệt thật.
            </div>
            <div className="d-flex flex-column gap-2">
              {botProposalSlots.map((slot) => (
                <Card
                  key={`bot-${slot.slotKey}`}
                  className="mb-2 border-warning"
                >
                  <Card.Header className="d-flex justify-content-between align-items-center py-2">
                    <div className="d-flex align-items-center gap-3">
                      <div className="fw-bold text-primary">{slot.hourLabel}</div>
                      {slot.slotTemplates.map((st, idx) => (
                        <Badge
                          key={idx}
                          bg="secondary"
                          className="text-white"
                        >
                          {st.name}
                        </Badge>
                      ))}
                    </div>
                    <Badge bg="warning">{slot.shifts.length} đề xuất</Badge>
                  </Card.Header>
                  <Card.Body className="py-2">
                    <div className="d-flex flex-column gap-2">{slot.shifts.map((shift) => renderShiftCard(shift))}</div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}
